import React, { useEffect, useState } from "react";
import { Cookie, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { getCookiePrefs, saveCookiePrefs } from "../lib/cookies";

import { tr } from "../i18n";
export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const existing = getCookiePrefs();
    if (!existing) {
      const t = setTimeout(() => setShowBanner(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Allow other parts of the site (e.g. the Cookie Policy page) to open settings
  useEffect(() => {
    const open = () => {
      const existing = getCookiePrefs();
      setPrefs({
        analytics: !!existing?.analytics,
        marketing: !!existing?.marketing,
      });
      setShowPrefs(true);
    };
    window.addEventListener("rh:open-cookie-settings", open);
    return () => window.removeEventListener("rh:open-cookie-settings", open);
  }, []);

  const commit = (p) => {
    const before = getCookiePrefs();
    saveCookiePrefs(p);
    setShowBanner(false);
    setShowPrefs(false);
    // If analytics was just enabled and PostHog hasn't been loaded yet, reload
    // so the guard in index.html initialises it.
    if (p.analytics && !(before && before.analytics) && !window.posthog?.__loaded) {
      setTimeout(() => window.location.reload(), 150);
    }
  };

  const acceptAll = () => commit({ analytics: true, marketing: true });
  const rejectAll = () => commit({ analytics: false, marketing: false });

  return (
    <>
      {/* Bottom consent banner */}
      {showBanner && (
        <div className="fixed bottom-0 inset-x-0 z-[100] px-4 pb-4">
          <div className="max-w-4xl mx-auto rounded-2xl bg-[#0b1f3a] text-white shadow-2xl shadow-black/30 ring-1 ring-[#c2a15a]/25 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie size={22} className="text-[#c2a15a] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-[15px]">{tr("We value your privacy")}</p>
                <p className="text-white/60 text-[13px] leading-relaxed mt-1">
                  {tr("We use strictly necessary cookies to run this site and, with your consent, analytics cookies to improve it. You can accept all, reject non-essential cookies, or customise your choices.")}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <button
                onClick={() => {
                  setShowBanner(false);
                  setPrefs({ analytics: false, marketing: false });
                  setShowPrefs(true);
                }}
                className="text-[13px] font-medium px-4 py-2.5 rounded-full border border-white/25 hover:border-[#c2a15a] hover:text-[#c2a15a] transition-colors"
              >
                {tr("Customise")}
              </button>
              <button
                onClick={rejectAll}
                className="text-[13px] font-medium px-4 py-2.5 rounded-full border border-white/25 hover:border-[#c2a15a] hover:text-[#c2a15a] transition-colors"
              >
                {tr("Reject non-essential")}
              </button>
              <button
                onClick={acceptAll}
                className="text-[13px] font-semibold px-4 py-2.5 rounded-full bg-[#c2a15a] hover:bg-[#d8bd82] text-[#0b1f3a] transition-colors"
              >
                {tr("Accept all")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences dialog */}
      <Dialog open={showPrefs} onOpenChange={(o) => !o && setShowPrefs(false)}>
        <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden gap-0 border-0">
          <div className="bg-[#0b1f3a] px-7 pt-7 pb-6">
            <DialogHeader>
              <p className="text-[#c2a15a] text-[11px] tracking-[0.25em] uppercase mb-2">
                {tr("Cookie settings")}
              </p>
              <DialogTitle className="font-display text-white text-[26px] leading-tight">
                {tr("Manage your preferences")}
              </DialogTitle>
              <DialogDescription className="text-white/60 text-[14px] mt-1">
                {tr("Choose which optional cookies we may use. Your choice is saved and respected on future visits.")}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-7 py-6 space-y-4">
            <PrefRow
              title={tr("Strictly necessary")}
              desc={tr("Required for the website to function (security, session, this consent choice). Always on.")}
              checked={true}
              disabled
              icon
            />
            <PrefRow
              title={tr("Analytics")}
              desc={tr("Help us understand how the site is used (PostHog). Optional.")}
              checked={prefs.analytics}
              onChange={(v) => setPrefs({ ...prefs, analytics: v })}
            />
            <PrefRow
              title={tr("Marketing")}
              desc={tr("Measure the effectiveness of our communications. Optional.")}
              checked={prefs.marketing}
              onChange={(v) => setPrefs({ ...prefs, marketing: v })}
            />
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => commit(prefs)}
                className="flex-1 bg-[#0b1f3a] hover:bg-[#163561] text-white font-semibold text-[14px] py-3.5 rounded-full transition-colors"
              >
                {tr("Save preferences")}
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 bg-[#c2a15a] hover:bg-[#d8bd82] text-[#0b1f3a] font-semibold text-[14px] py-3.5 rounded-full transition-colors"
              >
                {tr("Accept all")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PrefRow({ title, desc, checked, disabled, onChange, icon }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-[#0b1f3a]/10 px-4 py-3.5">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {icon && <ShieldCheck size={15} className="text-[#c2a15a]" />}
          <p className="font-medium text-[#0b1f3a] text-[14.5px]">{title}</p>
        </div>
        <p className="text-[13px] text-[#0b1f3a]/55 mt-1 leading-relaxed">{desc}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        className="mt-1 data-[state=checked]:bg-[#c2a15a]"
      />
    </div>
  );
}
